import React from 'react'
import {EntityLink} from 'common/Links'
import {User as UserIcon, Camera as CameraIcon} from 'react-feather'
import {cn} from 'utils'
import {useState, useMutation, useMe, useLocation} from 'hooks'
import {mutationUpdateProfilePicture} from 'graph'

/** The image / fallback shown inside any avatar. */
function AvatarImage({user}: any) {
	return user.profileImage ? (
		<img
			className="avatar-img rounded-circle"
			src={user.profileImage.uri}
			alt={`${user.username}'s avatar`}
			title={user.username}
		/>
	) : (
		<div className="avatar-title font-size-lg bg-primary-soft rounded-circle text-primary">
			<UserIcon size={17} />
		</div>
	)
}

/**
 * Editable avatar — click to change your profile picture. The whole avatar is a
 * file picker (label) with a camera badge; selecting an image uploads it via
 * `updateProfilePicture` (multipart) and the avatar refreshes from the returned
 * user. Used for the viewer's own avatar (e.g. the profile header).
 */
function EditableAvatar({user, size, className, ...rest}: any) {
	const [hover, setHover] = useState(false)
	const [doUpload, {loading}] = useMutation(mutationUpdateProfilePicture, {
		onError(error: any) {
			console.log('An error occured while uploading the profile picture', error)
		},
	})
	const onPick = (e: any) => {
		const file = e.target.files?.[0]
		if (file) doUpload({variables: {image: file}})
		e.target.value = ''
	}

	const show = hover || loading

	return (
		<label
			{...rest}
			className={cn('avatar', `avatar-${size}`, className)}
			style={{position: 'relative', display: 'inline-block', cursor: 'pointer'}}
			title="Change profile picture"
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
		>
			<AvatarImage user={user} />
			{/* On hover: darken the avatar and show a centered line camera icon. */}
			<span
				style={{
					position: 'absolute',
					inset: 0,
					borderRadius: '50%',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					color: '#fff',
					background: show ? 'rgba(0, 0, 0, 0.45)' : 'transparent',
					opacity: show ? 1 : 0,
					transition: 'opacity 0.15s ease, background 0.15s ease',
					pointerEvents: 'none',
				}}
			>
				<CameraIcon size={28} />
			</span>
			<input
				type="file"
				accept="image/jpeg,image/png"
				hidden
				disabled={loading}
				onChange={onPick}
			/>
		</label>
	)
}

export default function Avatar({user, size = 'sm', className, noLink, editable, ...rest}: any) {
	const me = useMe()
	const {pathname} = useLocation()

	if (editable) {
		return <EditableAvatar user={user} size={size} className={className} {...rest} />
	}

	// Avatars link to the user's profile — except when we're already there: on
	// that user's profile page (`/users/:username`), or on `/me` when this avatar
	// is the viewer themselves (effectively the same profile).
	const seg = pathname.split('/').filter(Boolean)
	const onThisProfile =
		(seg[0] === 'users' && seg[1] === user.username) || (seg[0] === 'me' && me?.id === user.id)
	const suppressLink = noLink || onThisProfile

	const Comp: any = suppressLink ? 'div' : EntityLink
	const props = suppressLink ? rest : {...rest, to: user}

	return (
		<Comp {...props} className={cn('avatar', `avatar-${size}`, className)}>
			<AvatarImage user={user} />
		</Comp>
	)
}
